import React, { useEffect } from "react";
import { Award, Code, ShieldCheck, Zap, ArrowRight, Briefcase, Activity } from "lucide-react";
import { Link } from "react-router-dom";

const itCertifications = [
  "AI For Everyone",
  "Programming For Everybody (Python)",
  "Introduction to Cloud",
  "Building Your Cybersecurity Toolkit",
  "Build a Serverless App with AWS Lambda",
  "React Tutorial and Project Course",
  "Node Tutorial and Project Course",
];

const ndtCertifications = [
  "NDT Level II: UT",
  "NDT Level II: RT",
  "NDT Level II: PT",
  "NDT Level II: MT",
  "PCN Level II: UT",
  "PCN Level II: PAUT",
];

const skills = {
  IT: [
    "Python & JavaScript (ES6+)",
    "React, Node.js & Express",
    "AWS Cloud & Serverless (Lambda)",
    "CI/CD & Git Workflow",
    "SaaS Platform Architecture",
  ],
  NDT: [
    "UT, RT, PT, MT Techniques",
    "Advanced PAUT & TOFD",
    "Calibration & Code Reporting",
    "Industrial Inspection Standards",
    "Safety & Compliance Protocol",
  ],
};

const experience = [
  {
    year: "2022 – 2025",
    company: "Godrej & Boyce",
    role: "Lead IT + NDT Platform",
    desc: "Spearheaded digital transformation for industrial workflows.",
  },
  {
    year: "2025 – Present",
    company: "Focus Group",
    role: "SaaS Architect & Founder",
    desc: "Scaling training portals and global NDT software solutions.",
  },
];

export default function Expertise() {
  useEffect(() => {
    document.title = "Omkar More – Skills & Certifications | IT & NDT Expert";
    const descriptionMeta = document.querySelector('meta[name="description"]');
    if (descriptionMeta) {
      descriptionMeta.setAttribute(
        "content",
        "Explore Omkar More's IT and NDT skills, certifications, and professional expertise in industrial inspections, SaaS platforms, and web development."
      );
    }
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.setAttribute(
        "content",
        "Omkar More, IT Skills, NDT Certifications, Industrial Inspection, SaaS, Full-Stack Development, React, Node.js"
      );
    }
  }, []);

  return (
<div className="bg-slate-50 min-h-screen pb-16 md:pb-24">
      
      {/* 1. HERO HEADER - Fluid Typography */}
      <section className="bg-white border-b border-slate-200 pt-24 md:pt-32 pb-12 md:pb-16 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
            <div className="max-w-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 md:w-10 h-[1px] bg-cyan-600" />
                <span className="font-mono text-[10px] md:text-xs tracking-[0.3em] md:tracking-[0.4em] uppercase font-black text-slate-400">
                  Capability Report
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black text-slate-950 tracking-tighter leading-[1.1] md:leading-none">
                Skills <span className="text-cyan-600 font-light">&</span><br className="hidden sm:block" /> Certifications
              </h1>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-12 md:mt-16">
        
        {/* 2. DUAL EXPERTISE GRID - Single col on mobile, two col on LG */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12">
          
          {/* IT SIDE */}
          <div className="space-y-6 md:space-y-8">
            <div className="flex items-center gap-4 mb-4 md:mb-8">
              <div className="p-2.5 md:p-3 bg-slate-950 rounded-xl text-white shadow-lg">
                <Code size={20} className="md:w-6 md:h-6" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-slate-950">
                Information Tech
              </h2>
            </div>

            {/* IT Certifications Card */}
            <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow group">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-mono text-[9px] md:text-[10px] font-black uppercase tracking-widest text-cyan-600">
                  Credential List
                </h3>
                <Activity size={14} className="text-slate-200 group-hover:text-cyan-500 transition-colors" />
              </div>
              <div className="grid grid-cols-1 gap-4">
                {itCertifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-3 group/item">
                    <div className="w-1.5 h-1.5 rounded-full bg-slate-200 group-hover/item:bg-cyan-500 transition-colors" />
                    <span className="text-sm md:text-base text-slate-600 group-hover/item:text-slate-950 font-medium transition-colors">
                      {cert}
                    </span>
                  </div>
                ))}
              </div>
              {/* Added Technical footer for consistency */}
              <div className="mt-8 pt-4 border-t border-slate-50 flex justify-between items-center opacity-50">
                 {/* <span className="text-[8px] font-mono uppercase tracking-tighter">System_Check: Optimal</span> */}
                 <div className="h-1 w-12 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 w-full" />
                 </div>
              </div>
            </div>

            {/* IT Skills Tags */}
            <div className="flex flex-wrap gap-2">
              {skills.IT.map((skill, idx) => (
                <span key={idx} className="px-3 md:px-4 py-1.5 md:py-2 bg-slate-200/50 text-slate-700 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-wide">
                  {skill}
                </span>
              ))}
            </div>
          </div>

          {/* NDT SIDE */}
          <div className="space-y-6 md:space-y-8">
            <div className="flex items-center gap-4 mb-4 md:mb-8">
              <div className="p-2.5 md:p-3 bg-cyan-600 rounded-xl text-white shadow-lg shadow-cyan-100">
                <ShieldCheck size={20} className="md:w-6 md:h-6" />
              </div>
              <h2 className="text-2xl md:text-3xl font-black uppercase tracking-tight text-slate-950">
                Industrial NDT
              </h2>
            </div>

            {/* NDT Certifications Card */}
            <div className="bg-white border border-slate-200 rounded-[2rem] md:rounded-3xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow group">
              <div className="flex justify-between items-center mb-6">
                <h3 className="font-mono text-[9px] md:text-[10px] font-black uppercase tracking-widest text-cyan-600">
                  Technical Licenses
                </h3>
                <div className="flex gap-1">
                  <div className="w-1 h-1 rounded-full bg-cyan-500 animate-pulse" />
                  <div className="w-1 h-1 rounded-full bg-cyan-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4">
                {ndtCertifications.map((cert, idx) => (
                  <div key={idx} className="flex items-center gap-3 group/item">
                    <Award size={16} className="text-slate-300 group-hover/item:text-cyan-600 transition-colors" />
                    <span className="text-sm md:text-base text-slate-600 group-hover/item:text-slate-950 font-medium transition-colors">
                      {cert}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-4 border-t border-slate-50 flex justify-between items-center opacity-50">
                 {/* <span className="text-[8px] font-mono uppercase tracking-tighter">Safety_Protocol: Active</span> */}
                 <div className="h-1 w-12 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-600 w-3/4" />
                 </div>
              </div>
            </div>

            {/* NDT Skills Tags */}
            <div className="flex flex-wrap gap-2">
              {skills.NDT.map((skill, idx) => (
                <span key={idx} className="px-3 md:px-4 py-1.5 md:py-2 bg-cyan-50 text-cyan-700 border border-cyan-100 rounded-lg text-[10px] md:text-xs font-bold uppercase tracking-wide">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* 3. PROFESSIONAL EXPERIENCE BAR - Bento Style Stacking */}
        <div className="mt-20 md:mt-28">
          <div className="flex flex-col items-center mb-10 md:mb-16">
            <h3 className="text-center font-mono text-[9px] md:text-[10px] font-black text-slate-400 uppercase tracking-[0.4em] md:tracking-[0.5em] mb-4">
              Leadership Timeline
            </h3>
            <div className="w-12 h-[2px] bg-slate-200" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {experience.map((exp, idx) => (
              <div key={idx} className="flex items-start gap-4 md:gap-6 bg-white p-6 md:p-8 rounded-[2rem] md:rounded-3xl border border-slate-100 hover:border-cyan-200 transition-all group">
                {/* Timeline vertical bar hidden on small mobile to save space */}
                <div className="hidden sm:flex flex-col items-center shrink-0">
                   <div className="w-10 h-10 md:w-12 md:h-12 bg-slate-50 rounded-full flex items-center justify-center text-slate-400 group-hover:text-cyan-600 group-hover:bg-cyan-50 transition-all">
                      <Briefcase size={18} />
                   </div>
                   <div className="w-px h-full bg-slate-100 mt-4 group-hover:bg-cyan-100 transition-colors" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono text-[10px] font-black text-cyan-600 uppercase tracking-widest">
                      {exp.year}
                    </span>
                    <div className="h-[1px] w-4 bg-slate-200 sm:hidden" />
                  </div>
                  <h4 className="text-lg md:text-xl font-black text-slate-900 mb-1 uppercase tracking-tight">
                    {exp.company}
                  </h4>
                  <p className="font-bold text-slate-400 text-[10px] md:text-xs uppercase mb-4 tracking-tighter italic">
                    {exp.role}
                  </p>
                  <p className="text-slate-500 text-sm md:text-base leading-relaxed">
                    {exp.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 4. CALL TO ACTION - Mobile Full Width */}
        <div className="mt-20 md:mt-24 text-center">
            <Link 
                to="/contact" 
                className="inline-flex w-full sm:w-auto items-center justify-center gap-4 bg-slate-950 text-white px-8 md:px-10 py-4 md:py-5 rounded-full font-black text-[10px] md:text-xs uppercase tracking-[0.2em] hover:bg-cyan-600 transition-all active:scale-95 shadow-2xl shadow-slate-200"
            >
                Inquire for Collaboration <ArrowRight size={16} />
            </Link>
        </div>
      </div>
    </div>
  );
}